import { action, computed, observable, runInAction } from "mobx";
import * as validator from 'validator';

import { educationEditorStore, jobHistoryEditorStore, selfEvaluationEditorStore } from ".";
import request from "../common/utils/Request";

import {
  DataLoadStatus,
  EducationsGetData,
  Gender,
  InputHighlight,
  JobsGetData,
  ProgramTopicsGetData,
  SelfEvaluationScoreGetData,
  UserProfileGetData
} from "../types";

class ProfileStore {
  // Private instance
  private static _instance: ProfileStore;

  @observable public awaitServer: boolean = false;
  @observable public city: string;
  @observable public cityChanged: boolean = false;
  @observable public email: string;
  @observable public firstName: string;
  @observable public firstNameChanged: boolean = false;
  @observable public gender: Gender;
  @observable public interests: string;
  @observable public interestsChanged: boolean = false;
  @observable public lastName: string;
  @observable public lastNameChanged: boolean = false;
  @observable public loadStatus: DataLoadStatus = DataLoadStatus.NOT_STARTED;
  @observable public state: string;
  @observable public stateChanged: boolean = false;

  /**
   * Use ProfileStore.instance to retrieve an instance of this class.
   */
  private constructor() { }

  /**
   * Instance of ProfileStore.
   */
  public static get instance() {
    if (!this._instance) {
      this._instance = new ProfileStore();
    }

    return this._instance;
  }

  @computed
  public get isCityValid(): boolean {
    return !!this.city && !validator.isEmpty(this.city);
  }

  @computed
  public get isFirstNameValid(): boolean {
    return !!this.firstName && !validator.isEmpty(this.firstName);
  }

  @computed
  public get isLastNameValid(): boolean {
    return !!this.lastName && !validator.isEmpty(this.lastName);
  }

  @computed
  public get isStateValid(): boolean {
    return !!this.state && !validator.isEmpty(this.state);
  }
  
  @computed
  public get cityHelpMessage(): string {
    return this.isCityValid ? '' : 'Please enter city';
  }

  @computed
  public get firstNameHelpMessage(): string {
    return this.isFirstNameValid ? '' : 'Please enter first name';
  }

  @computed
  public get lastNameHelpMessage(): string {
    return this.isLastNameValid ? '' : 'Please enter last name';
  }

  @computed
  public get stateHelpMessage(): string {
    return this.isStateValid ? '' : 'Please enter state';
  }

  @computed
  public get cityHighlight(): InputHighlight {
    return this.isCityValid ? InputHighlight.CORRECT : InputHighlight.DANGER;
  }

  @computed
  public get firstNameHighlight(): InputHighlight {
    return this.isFirstNameValid ? InputHighlight.CORRECT : InputHighlight.DANGER;
  }

  @computed
  public get lastNameHighlight(): InputHighlight {
    return this.isLastNameValid ? InputHighlight.CORRECT : InputHighlight.DANGER;
  }

  @computed
  public get stateHighlight(): InputHighlight {
    return this.isStateValid ? InputHighlight.CORRECT : InputHighlight.DANGER;
  }

  @action
  public saveProfileData = async () => {
    this.awaitServer = true;
  }

  /**
   * Make a get request to server for the user profile.
   */
  @action
  public loadProfileData = async () => {
    try {
      this.loadStatus = DataLoadStatus.LOADING;

      await Promise.all([
        this.loadEducations(),
        this.loadJobs(),
        this.loadProfile(),
        this.loadSelfEvaluationScores()
      ]);

      runInAction(() => this.loadStatus = DataLoadStatus.LOADED);
    } catch (err) {
      runInAction(() => this.loadStatus = DataLoadStatus.FAILED);
      console.error('error:', err);
    }
  }

  @action
  private loadEducations = async () => {
    const educationPromise = await request.get('/educations/');
    const educations: EducationsGetData[] = educationPromise.data;
    const retrievedEducations = educations.map(education => ({
      degree: education.degree,
      gradYear: education.year_graduated,
      id: education.id,
      major: education.major,
      school: education.school
    }));

    runInAction(() => educationEditorStore.educations = retrievedEducations);
  }

  @action
  private loadJobs = async () => {
    const jobHistoryPromise = await request.get('/jobs/');
    const jobHistories: JobsGetData[] = jobHistoryPromise.data;
    const retrievedJobHistories = jobHistories.map(job => ({
      category: job.category,
      id: job.id,
      level: job.level,
      title: job.role,
      years: job.years
    }));

    runInAction(() => jobHistoryEditorStore.jobs = retrievedJobHistories);
  }

  @action
  private loadProfile = async () => {
    const userProfilePromise = await request.get('/user/profile/');
    const userProfile: UserProfileGetData = userProfilePromise.data;

    runInAction(() => {
      this.city = userProfile.city;
      this.email = userProfile.email;
      this.firstName = userProfile.first_name;
      this.interests = userProfile.interests;
      this.lastName = userProfile.last_name;
      this.state = userProfile.state_province;

      switch (userProfile.gender) {
        case 'F': this.gender = Gender.FEMALE; break;
        case 'M': this.gender = Gender.MALE; break;
        default: this.gender = Gender.OTHER;
      }

      selfEvaluationEditorStore.question = userProfile.questions;
    });
  }

  @action
  private loadProgramTopics = async () => {
    const programTopicsPromise = await request.get('/program_topics/');
    const programTopics: ProgramTopicsGetData[] = programTopicsPromise.data;
    runInAction(() => selfEvaluationEditorStore.programTopics = programTopics);
  }

  @action
  private loadSelfEvaluationScores = async () => {
    await this.loadProgramTopics();
    const selfEvaluationPromise = await request.get('/self_evaluation_scores/');
    const selfEvaluationData: SelfEvaluationScoreGetData[] = selfEvaluationPromise.data;

    selfEvaluationEditorStore.selfEvaluations = selfEvaluationData.map(score => {
      const topic = selfEvaluationEditorStore.programTopics
          .find(programTopic => programTopic.id === score.program_topics)!;

      return {
        id: score.id,
        programId: score.program_topics,
        programName: topic.name,
        score: score.score
      };
    });
  }
}

export default ProfileStore;
export const profileStore = ProfileStore.instance;


// WEBPACK FOOTER //
// ./src/stores/ProfileStore.ts